'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { useIsClient } from '@/hooks';

/**
 * Props for `FeatureWithImageSideBySide`.
 */
export type FeatureWithImageSideBySideProps =
  SliceComponentProps<Content.FeatureWithImageSideBySideSlice>;

/**
 * Component for "FeatureWithImageSideBySide" Slices.
 * Side-by-side layout with text content on one side and image on the other.
 */
const FeatureWithImageSideBySide: FC<FeatureWithImageSideBySideProps> = ({
  slice,
}) => {
  const { background, title, description, footnote, main_image } =
    slice.primary;

  const contentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: contentRef, delay: '0ms' },
      { ref: imageRef, delay: '200ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    return () => {
      elementsToAnimate.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [isClient]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative py-[80px] px-4 md:px-8 lg:px-[150px]"
      style={{ backgroundColor: '#002726' }}
    >
      {/* Background Image Layer */}
      {isFilled.image(background) && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <PrismicNextImage
            field={background}
            className="absolute w-full h-[226%] left-0 top-[-12%] object-cover max-w-none"
            fill={false}
            priority
          />
        </div>
      )}

      {/* Main Content Container */}
      <div className="relative max-w-[1140px] mx-auto flex flex-col lg:flex-row gap-10 lg:gap-[60px] items-start justify-center">
        {/* Text Content - Left Side */}
        <div
          ref={contentRef}
          className="flex-1 flex flex-col gap-[40px] items-start justify-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {/* Title */}
          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-white w-full">
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {/* Description */}
          {isFilled.richText(description) && (
            <div className="w-full text-lg md:text-xl font-medium leading-[1.4] text-white space-y-4">
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="mb-0">{children}</p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-bold">{children}</strong>
                  ),
                  em: ({ children }) => <em className="italic">{children}</em>,
                  hyperlink: ({ children, node }) => (
                    <a
                      href={node.data.url}
                      className="text-[#05f273] hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                }}
              />
            </div>
          )}

          {/* Footnote */}
          {isFilled.richText(footnote) && (
            <PrismicRichText
              field={footnote}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-lg md:text-xl font-medium leading-[1.4] text-white">
                    {children}
                  </p>
                ),
              }}
            />
          )}
        </div>

        {/* Image - Right Side */}
        {isFilled.image(main_image) && (
          <div
            ref={imageRef}
            className="w-full lg:w-[460px] shrink-0 flex items-center justify-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            <PrismicNextImage
              field={main_image}
              className="w-full h-auto object-contain"
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default FeatureWithImageSideBySide;
